import { Box, Button, keyframes, Typography, useTheme } from "@mui/material";
import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import useSWRMutation from "swr/mutation";
import { addCommas, digitsEnToFa } from "@persian-tools/persian-tools";
import { blueGrey } from "@mui/material/colors";
import { fetcherPost } from "../../../services/fetcher";
import { convertToJalali } from "../../../utils/functions";
import { IOrder } from "../../../types/driver";
import DetailsLable from "../Driver/GetCargo/FreightsList/FreightCard/Details/DetailsLable";
import MapBoxBottomSheet from "../Driver/GetCargo/FreightsList/FreightCard/Details/MapBoxBottomSheet";
import LoadingBox from "../../components/LoadingBox";
import CustomButton from "../../components/CustomButton";
import Boxoutline from "../../../media/images/icons/outline-box";
import BigCarSpeed from "../../../media/images/icons/bigcar-speed";
import BigCarIcon2 from "../../../media/images/icons/big-car-2";
import DolorGreen from "../../../media/images/icons/dolor-green";
import ArrowStepLeft from "../../../media/images/icons/arrow-steps-register";
import LocationFill from "../../../media/images/icons/location-fill";
import CalendarCheck from "../../../media/images/icons/calendar-check";
import ArrowTickDown from "../../../media/images/icons/arrow-tick-down";
import ArrowTickLeft from "../../../media/images/icons/arrow-tick-left";
import LocationSVG from "../../../media/images/icons/location";
import CallSVG from "../../../media/images/icons/call";
import BoxChecked from "../../../media/images/icons/boxchecked";
import GetCargoModal from "../Driver/GetCargo/FreightsList/FreightCard/GetCargoModal";
import DriverLoginModal from "./DriverLoginModal";

const scale = keyframes`
  from { scale: 0.8; }
  to   { scale: 1;   }
`;

const orderAny = (o: IOrder) => o as IOrder & Record<string, unknown>;

export default function CargoDetailPage() {
  const theme = useTheme();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();

  const [order, setOrder] = useState<IOrder | null>(null);
  const [notFound, setNotFound] = useState(false);
  const [open, setOpen] = useState(false);
  const [mapOpen, setMapOpen] = useState(false);
  const [getCargoModal, setGetCargoModal] = useState(false);
  const [loginModal, setLoginModal] = useState(false);

  const { trigger: getOrderById, isMutating: isLoading } = useSWRMutation<
    IOrder,
    Error,
    [string, string | null],
    { id: string }
  >(
    [`/Order/GetById`, localStorage.getItem("access_token")],
    fetcherPost,
    {
      onSuccess: (data: IOrder) => { setOrder(data); setNotFound(false); },
      onError: () => { setNotFound(true); setOrder(null); },
    }
  );

  useEffect(() => {
    if (id) getOrderById({ id });
  }, [id, getOrderById]);

  const requestTakeCargo = () => {
    const token = localStorage.getItem("access_token");
    if (!token) {
      setLoginModal(true);
      return;
    }
    setGetCargoModal(true);
  };

  if (isLoading) return <LoadingBox />;

  if (notFound || !order) {
    return (
      <Box sx={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", padding: 3 }}>
        <Typography sx={{ fontSize: "16px", fontWeight: "700" }} color="text.secondary">
          بار مورد نظر یافت نشد.
        </Typography>
      </Box>
    );
  }

  const o = orderAny(order);
  const companyTel = order.companyTel || (o.companyTel as string) || (o["CompanyTel"] as string);
  const truckWeightTo = order.truckWeightTo ?? (o.truckWeightTo as number | null | undefined) ?? (o["TruckWeightTo"] as number | null | undefined);
  const carryType = order.orderCarryType || (o["OrderCarryType"] as string) || (o["orderCarryType"] as string);

  return (
    <Box sx={{ flex: 1, paddingX: "8px", paddingTop: "16px", paddingBottom: "20px", overflowY: "auto" }}>
      {/* Back button */}
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
        <Typography sx={{ fontSize: "16px", fontWeight: "700", color: "secondary.main" }}>
          جزئیات بار
        </Typography>
        <CustomButton
          onClick={() => {
            if (localStorage.getItem("access_token")) {
              navigate("/driver/dashboard", { replace: true });
              return;
            }
            navigate(-1);
          }}
          flexDirection="row"
          title="بازگشت"
          justifyContent="center"
          bgColor={theme.palette.secondary[50]}
          textColor="secondary.main"
          fSize="14px"
          px="12px"
          py="8px"
          borderRadius="10px"
          icon2={<ArrowTickLeft size={16} color={theme.palette.secondary.main} />}
        />
      </Box>

      {/* Card — same design as CargoCard */}
      <Box
        sx={{
          boxShadow: "0px 4px 6px 0px rgba(0, 0, 0, 0.10)",
          display: "flex",
          borderColor: blueGrey[100],
          borderWidth: "1px",
          borderStyle: "solid",
          borderRadius: "16px",
          flexDirection: "column",
          padding: "8px",
          backgroundColor: "bgColor.white",
        }}
      >
        {/* Icon + goodsName + truckCount */}
        <Box sx={{ display: "flex", gap: "4px" }}>
          <Box
            sx={{
              backgroundColor: "bgColor.blueGrey50",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: "6px",
              borderRadius: "8px",
            }}
          >
            <Boxoutline color={theme.palette.textColor.main} />
          </Box>
          <Box sx={{ flex: 1, display: "flex", flexDirection: "column", gap: "4px" }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "start" }}>
              <Typography sx={{ fontWeight: "600" }}>{order.goodsName}</Typography>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  backgroundColor: theme.palette.primary[50],
                  padding: "4px",
                  borderRadius: "6px",
                  gap: "4px",
                }}
              >
                <BigCarSpeed size={16} color={theme.palette.primary.main} />
                <Typography sx={{ fontSize: "12px", color: "primary.main" }}>{order.truckCount}</Typography>
              </Box>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  backgroundColor: theme.palette.primary[50],
                  padding: "4px",
                  borderRadius: "8px",
                  gap: "4px",
                }}
              >
                <BigCarIcon2 color={theme.palette.primary.main} />
                <Typography sx={{ fontSize: "12px", fontWeight: "700", color: "primary.main" }}>
                  {order.loaderTypeGroup}
                </Typography>
              </Box>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  backgroundColor: "success.light",
                  padding: "4px",
                  borderRadius: "6px",
                }}
              >
                <DolorGreen color={theme.palette.success.main} />
                <Typography sx={{ color: "success.main" }}>{addCommas(order.fare)} ریال</Typography>
              </Box>
            </Box>
          </Box>
        </Box>

        {/* Source → Destination */}
        <Box
          sx={{
            mt: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            backgroundColor: theme.palette.bgColor.grey100,
            paddingY: "8px",
            paddingX: "4px",
            borderRadius: "8px",
            borderColor: blueGrey[100],
            borderWidth: "1px",
            borderStyle: "solid",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", flex: 1, justifyContent: "center" }}>
            <LocationFill size={22} color={theme.palette.primary.main} />
            <Typography sx={{ fontWeight: "700", fontSize: "14px" }}>{order.source}</Typography>
          </Box>
          <ArrowStepLeft color={theme.palette.textColor.main} />
          <Box sx={{ display: "flex", alignItems: "center", flex: 1, gap: "4px", justifyContent: "center" }}>
            <LocationFill size={22} color={theme.palette.secondary.main} />
            <Typography sx={{ fontWeight: "700", fontSize: "14px" }}>{order.destination}</Typography>
          </Box>
        </Box>

        {/* orderCarryType badge — always rendered, same as CargoCard */}
        <Box
          sx={{
            mt: 1,
            display: "flex",
            alignItems: "center",
            backgroundColor: theme.palette.primary[50],
            padding: "4px",
            borderRadius: "6px",
            gap: "4px",
            alignSelf: "flex-start",
          }}
        >
          <BigCarSpeed size={16} color={theme.palette.primary.main} />
          <Typography sx={{ fontSize: "12px", color: "primary.main" }}>
            {carryType || "-"}
          </Typography>
        </Box>

        <DriverLoginModal
          open={loginModal}
          onClose={() => setLoginModal(false)}
          onLoggedIn={() => {
            setLoginModal(false);
            setGetCargoModal(true);
          }}
        />
        <GetCargoModal
          open={getCargoModal}
          handleClose={() => setGetCargoModal(false)}
          onUnauthorized={() => {
            setGetCargoModal(false);
            setLoginModal(true);
          }}
          {...order}
        />

        {/* Bottom action row — toggle + دریافت بار (no پیشنهاد کرایه) */}
        <Box sx={{ mt: 1, display: "flex" }}>
          <Box
            sx={{
              width: "80px",
              transition: "all 0.3s",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              borderRadius: "8px 8px 0px 0px",
              overflow: "hidden",
              padding: "4px",
              backgroundColor: open ? "bgColor.grey100" : "",
              boxShadow: open
                ? "4px 4px 5px -4px rgba(0,0,0,0.1) inset, -4px 4px 5px -4px rgba(0,0,0,0.1) inset"
                : "",
            }}
          >
            <Button
              onClick={() => setOpen((prev) => !prev)}
              sx={{
                width: "100%",
                display: "flex",
                flexDirection: "column",
                borderRadius: "6px",
                alignItems: "center",
                border: "1px solid",
                minWidth: "0px !important",
                height: "fit-content",
                padding: "8px",
                borderColor: open
                  ? `${theme.palette.error.main} !important`
                  : `${theme.palette.secondary.main} !important`,
                backgroundColor: open
                  ? `${theme.palette.error.light} !important`
                  : `${theme.palette.secondary[50]} !important`,
                "& .MuiTouchRipple-root": {
                  color: `${theme.palette.secondary.main} !important`,
                },
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "4px",
                  animation: open ? "" : `${scale} alternate 1s infinite`,
                }}
              >
                <Typography sx={{ color: open ? "error.main" : "secondary.main", whiteSpace: "nowrap", fontSize: "12px" }}>
                  {open ? "بستن جزییات" : "جزییات بار"}
                </Typography>
                <Box sx={{ transform: open ? "rotate(180deg)" : "", transition: "all 0.3s", display: "flex", justifyContent: "center" }}>
                  <ArrowTickDown size={14} color={open ? theme.palette.error.main : theme.palette.secondary.main} />
                </Box>
              </Box>
            </Button>
          </Box>
          {/* دریافت بار */}
          <Box
            sx={{
              flex: 1,
              display: "flex",
              gap: "8px",
              paddingY: "8px",
              paddingRight: "8px",
              zIndex: "50",
            }}
          >
            <CustomButton
              flex={1}
              flexDirection="row-reverse"
              onClick={requestTakeCargo}
              title="دریافت بار"
              justifyContent="center"
              bgColor="success.main"
              textColor="#fff"
              fSize="16px"
              px="10px"
              py="14px"
              borderRadius="10px"
              icon1={<BoxChecked size={26} color={"#fff"} />}
            />
          </Box>
        </Box>

        {/* Expandable details section */}
        <Box
          sx={{
            maxHeight: open ? "600px" : "0px",
            transition: "max-height 0.3s",
            overflow: "hidden",
            overflowY: open ? "auto" : "hidden",
            display: "flex",
            flexDirection: "column",
            gap: "6px",
            backgroundColor: "bgColor.grey100",
            borderRadius: "8px 0px 8px 8px",
            paddingX: "8px",
            boxShadow:
              "8px -4px 5px -8px rgba(0,0,0,0.1) inset , 4px -2px 5px -3px rgba(0,0,0,0.05) inset, -4px -6px 10px -4px rgba(0,0,0,0.1) inset",
          }}
        >
          <MapBoxBottomSheet
            getAddress={(str) => console.log(str)}
            open={mapOpen}
            onDismiss={() => setMapOpen(false)}
            center={
              order?.ladingLocationInfoLat && order?.ladingLocationInfoLng
                ? { lat: order.ladingLocationInfoLat, lng: order.ladingLocationInfoLng }
                : null
            }
          />

          <Box sx={{ display: "flex", pt: 2, gap: "8px" }}>
            <Box sx={{ flex: 1, display: "flex", flexDirection: "column", gap: "8px" }}>
              <DetailsLable>
                <Typography sx={{ fontSize: "14px", fontWeight: "700" }}>تاریخ ثبت سفارش:</Typography>
                <Typography sx={{ alignSelf: "end", fontSize: "14px", fontWeight: "700" }} color="primary.main">
                  {order.orderDateEn ? convertToJalali(order.orderDateEn) : (o.orderDate as string) || "-"}
                </Typography>
              </DetailsLable>
              <DetailsLable>
                <Typography sx={{ fontSize: "14px", fontWeight: "700" }}>شرکت حمل و نقل:</Typography>
                <Typography sx={{ alignSelf: "end", fontSize: "14px", fontWeight: "700" }} color="primary.main">
                  {order.companyName || "-"}
                </Typography>
              </DetailsLable>
            </Box>
            <Box sx={{ display: "flex", flexDirection: "column", gap: "8px" }}>
              <CustomButton
                onClick={() => setMapOpen(true)}
                flexDirection="row"
                title="محل بارگیری"
                justifyContent="center"
                borderColor="secondary.main"
                bgColor={theme.palette.secondary[50]}
                textColor="secondary.main"
                fSize="10px"
                px="10px"
                py="10px"
                borderRadius="10px"
                icon1={<LocationSVG size={16} color={theme.palette.secondary.main} />}
              />
              {companyTel?.length > 0 && (
                <a href={`tel:${companyTel}`} style={{ textDecoration: "none" }}>
                  <CustomButton
                    flexDirection="row"
                    title="اطلاعات تماس"
                    justifyContent="center"
                    borderColor="success.main"
                    bgColor={theme.palette.success.light}
                    textColor="success.main"
                    fSize="10px"
                    px="10px"
                    py="10px"
                    borderRadius="10px"
                    icon1={<CallSVG size={16} color={theme.palette.success.main} />}
                  />
                </a>
              )}
            </Box>
          </Box>

          <Box sx={{ display: "flex", gap: "8px" }}>
            <DetailsLable>
              <Typography sx={{ fontSize: "14px", fontWeight: "700" }}>شماره سفارش:</Typography>
              <Typography sx={{ alignSelf: "end", fontSize: "14px", fontWeight: "700" }} color="secondary.main">
                {order.orderId || "-"}
              </Typography>
            </DetailsLable>
            <DetailsLable>
              <Box sx={{ display: "flex", alignItems: "center", gap: "4px" }}>
                <CalendarCheck size={16} color={theme.palette.textColor.dark} />
                <Typography sx={{ fontSize: "14px", fontWeight: "700" }}>بسته بندی:</Typography>
              </Box>
              <Typography sx={{ alignSelf: "end", fontSize: "14px", fontWeight: "700" }} color="secondary.main">
                {order.packageTypeName || "-"}
              </Typography>
            </DetailsLable>
          </Box>

          <Box sx={{ display: "flex", gap: "8px" }}>
            <DetailsLable>
              <Typography sx={{ fontSize: "14px", fontWeight: "700" }}>تعداد:</Typography>
              <Typography sx={{ alignSelf: "end", fontSize: "14px", fontWeight: "700" }} color="secondary.main">
                {order.remainder != null ? `${order.remainder} عدد` : "-"}
              </Typography>
            </DetailsLable>
            <DetailsLable>
              <Box sx={{ display: "flex", alignItems: "center", gap: "4px" }}>
                <CalendarCheck size={16} color={theme.palette.textColor.dark} />
                <Typography sx={{ fontSize: "14px", fontWeight: "700" }}>تناژ:</Typography>
              </Box>
              <Typography sx={{ alignSelf: "end", fontSize: "14px", fontWeight: "700" }} color="secondary.main">
                {truckWeightTo != null
                  ? `از ${digitsEnToFa((order.truckWeight ?? 0) / 1000)} تن تا ${digitsEnToFa(truckWeightTo / 1000)} تن`
                  : order.weight != null
                  ? `${digitsEnToFa(order.weight)} تن`
                  : "-"}
              </Typography>
            </DetailsLable>
          </Box>

          <Box>
            <DetailsLable>
              <Box sx={{ display: "flex" }}>
                <CalendarCheck size={16} color={theme.palette.textColor.dark} />
                <Typography sx={{ fontSize: "14px", fontWeight: "700" }}>محل بارگیری:</Typography>
              </Box>
              <Typography sx={{ fontSize: "14px", fontWeight: "700" }} color="primary.main">
                {order.ladingLocationInfoAddress || (o.ladingLocationAddress as string) || "-"}
              </Typography>
            </DetailsLable>
          </Box>

          <Box sx={{ mb: 1, width: "100%", minWidth: 0 }}>
            <DetailsLable>
              <Box sx={{ display: "flex", flexDirection: "column", gap: "8px", mb: 8, minWidth: 0, width: "100%" }}>
                <Typography sx={{ fontSize: "14px", fontWeight: "700" }} color="secondary.main">
                  توضیحات بار:
                </Typography>
                <Typography
                  sx={{
                    fontSize: "14px",
                    fontWeight: "700",
                    whiteSpace: "normal",
                    overflowWrap: "break-word",
                    wordBreak: "break-word",
                  }}
                >
                  {order.description || "-"}
                </Typography>
              </Box>
            </DetailsLable>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
